// src/pages/TechnicianMaintenancePage.jsx
import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/TechnicianMaintenance.css";
import Select from "react-select";

export default function TechnicianMaintenancePage() {
  const { user } = useContext(AuthContext);

  const [finalizingId, setFinalizingId] = useState(null);
  const [finalStatusDraft, setFinalStatusDraft] = useState({});

  const isAdmin =
    user?.role === "admin" || user?.role === "super-admin" || user?.role === "Manager";
  const isTechnician = user?.role === "technician";
  const isSuperAdmin = user?.role === "super-admin" || user?.role === "Manager";

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const isBrowser = typeof window !== "undefined";
  const isLocalhost = isBrowser && window.location?.hostname === "localhost";
  const apiBase =
    process.env.REACT_APP_SERVER_URL || (isLocalhost ? "http://localhost:5000" : "");

  // photo URLs are stored relative to the backend (e.g. "/uploads/xxx.jpg");
  // resolve them against the backend origin so opening/viewing them doesn't
  // depend on the CRA dev-server proxy (which only forwards non-navigation requests).
  const makeFullUrl = (rawUrl) => {
    if (!rawUrl) return "#";
    if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
    if (!isLocalhost) return `/${rawUrl.replace(/^\/+/, "")}`;
    const base = (apiBase || "").replace(/\/$/, "");
    const path = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    return base ? `${base}${path}` : rawUrl;
  };

  // ✅ toggle create modal visibility
  const [showCreate, setShowCreate] = useState(false);

  // meta + sites + works + master sites + users
  const [meta, setMeta] = useState({ statuses: [], actions: [], parts: [] });
  const [sites, setSites] = useState([]);
  const [works, setWorks] = useState([]);
  const [masterSites, setMasterSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const partOptions = useMemo(
    () =>
      (meta.parts || []).map((p) => ({
        value: p._id,
        label: p.label,
      })),
    [meta.parts]
  );

  // ----- CREATE: target selector (site | work)
  const [targetType, setTargetType] = useState("site");

  // form state (✅ keep all fields)
  const [form, setForm] = useState({
    masterSiteId: "",
    siteId: "",
    workId: "",
    initialStatus: "",
    action: "",
    items: [{ partId: "", operation: "replace", qty: 1, unitPriceINR: "" }],
    notes: "",
    closeNow: false,
    finalStatus: "",
    sitePhoto: null,

    // telemetry
    rawWaterTDS: "",
    finalWaterTDS: "",
    HPPressure: "",
    GoodWaterFlow: "",
    RejectedWaterFlow: "",
    rawWaterTDSNotMeasured: false,
    finalWaterTDSNotMeasured: false,
    HPPressureNotMeasured: false,
    GoodWaterFlowNotMeasured: false,
    RejectedWaterFlowNotMeasured: false,
    SMSsending: false,
    backwashPump: false,
  });

  const [siteSearch, setSiteSearch] = useState("");
  const [workSearch, setWorkSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const sitePhotoInputRef = useRef(null);

  // ----- LOGS: state + filters
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilters, setLogsFilters] = useState({
    masterSiteId: "",
    siteId: "",
    technicianId: "",
    workId: "",
    from: "",
    to: "",
    limit: 25,
  });

  // radio to explicitly pick logs target (affects table columns + filter UX)
  const [logsTargetType, setLogsTargetType] = useState("site");

  // initial load
  useEffect(() => {
    (async () => {
      try {
        setLoadingMeta(true);
        const [mRes, sRes, wRes, msRes, uRes] = await Promise.all([
          axios.get("/api/maintenance/meta", authHeader),
          axios.get("/api/sites", authHeader),
          axios.get("/api/works", authHeader),
          axios.get("/api/mastersites", authHeader),
          axios.get("/api/users", authHeader),
        ]);
        setMeta(mRes.data || { statuses: [], actions: [], parts: [] });
        setSites(sRes.data || []);
        setWorks(wRes.data || []);
        setMasterSites(msRes.data || []);
        setUsers(
          (uRes.data || []).filter(
            (u) =>
              u.role === "technician" ||
              u.role === "admin" ||
              u.role === "super-admin" ||
              u.role === "Manager"
          )
        );
      } catch (err) {
        console.error("Failed to load meta/sites/works/users", err);
        alert("Failed to load metadata or lists. Check login and API.");
      } finally {
        setLoadingMeta(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ close modal on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setShowCreate(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ✅ lock background page scroll while the modal is open (mobile: prevents
  // the page behind the modal from scrolling instead of the modal itself).
  // Locking with position:fixed also resets scroll to top, so save/restore it.
  useEffect(() => {
    if (showCreate) {
      const scrollY = window.scrollY;
      document.body.dataset.scrollLock = String(scrollY);
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add("tm-modal-open");
    } else {
      const scrollY = Number(document.body.dataset.scrollLock || 0);
      document.body.classList.remove("tm-modal-open");
      document.body.style.top = "";
      delete document.body.dataset.scrollLock;
      window.scrollTo(0, scrollY);
    }
    return () => {
      document.body.classList.remove("tm-modal-open");
      document.body.style.top = "";
    };
  }, [showCreate]);

  // ===== derived =====
  const partsById = useMemo(() => {
    const map = new Map();
    (meta.parts || []).forEach((p) => map.set(String(p._id), p));
    return map;
  }, [meta.parts]);

  const sitesForSelectedMaster = useMemo(() => {
    const msId = logsFilters.masterSiteId;
    if (!msId) return sites;
    return (sites || []).filter((s) => String(s.masterSiteId) === String(msId));
  }, [sites, logsFilters.masterSiteId]);

  const filteredSitesForForm = useMemo(() => {
    let list = sites || [];
    if (form.masterSiteId) {
      list = list.filter((s) => String(s.masterSiteId) === String(form.masterSiteId));
    }
    if (siteSearch) {
      const q = siteSearch.toLowerCase();
      list = list.filter((s) => (s.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [siteSearch, sites, form.masterSiteId]);

  const filteredWorksForForm = useMemo(() => {
    if (!workSearch) return works;
    const q = workSearch.toLowerCase();
    return (works || []).filter((w) => (w.name || "").toLowerCase().includes(q));
  }, [workSearch, works]);

  const lineItems = useMemo(() => {
    return (form.items || []).map((it) => {
      const p = partsById.get(String(it.partId));
      const qty = Math.max(1, Number(it.qty || 1));
      const isRepair = it.operation === "repair";
      const unit = isRepair ? Number(it.unitPriceINR || 0) : Number(p?.priceINR ?? 0);
      const subtotal = unit * qty;
      return {
        ...it,
        unitPriceINR: isRepair ? it.unitPriceINR : unit,
        previewUnit: unit,
        subtotalINR: subtotal,
      };
    });
  }, [form.items, partsById]);

  const totalPartsINR = useMemo(
    () => lineItems.reduce((s, it) => s + (Number(it.subtotalINR) || 0), 0),
    [lineItems]
  );

  const siteMap = useMemo(() => {
    const m = new Map();
    (sites || []).forEach((s) => m.set(String(s._id), s));
    return m;
  }, [sites]);

  const workMap = useMemo(() => {
    const m = new Map();
    (works || []).forEach((w) => m.set(String(w._id), w));
    return m;
  }, [works]);

  const masterMap = useMemo(() => {
    const m = new Map();
    (masterSites || []).forEach((ms) => m.set(String(ms._id), ms));
    return m;
  }, [masterSites]);

  const displayedLogs = useMemo(() => {
    if (!Array.isArray(logs)) return [];
    return logs.filter((l) => (logsTargetType === "site" ? !!l?.siteId : !!l?.workId));
  }, [logs, logsTargetType]);

  const logsTotals = useMemo(() => {
    let totalPartsINR = 0,
      count = 0;
    for (const l of displayedLogs || []) {
      totalPartsINR += Number(l?.totalPartsINR || 0);
      count += 1;
    }
    return { totalPartsINR, count };
  }, [displayedLogs]);

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams();
      if (logsTargetType === "site") {
        if (logsFilters.masterSiteId) params.append("masterSiteId", logsFilters.masterSiteId);
        if (logsFilters.siteId) params.append("siteId", logsFilters.siteId);
      } else {
        if (logsFilters.workId) params.append("workId", logsFilters.workId);
      }
      if (isAdmin && logsFilters.technicianId) params.append("technicianId", logsFilters.technicianId);
      if (logsFilters.from) params.append("from", logsFilters.from);
      if (logsFilters.to) params.append("to", logsFilters.to);
      if (logsFilters.limit) params.append("limit", String(logsFilters.limit));
      const res = await axios.get(`/api/maintenance/logs?${params.toString()}`, authHeader);
      setLogs(res.data || []);
    } catch (err) {
      console.error("Failed to load logs", err);
      alert("Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  }, [
    authHeader,
    isAdmin,
    logsTargetType,
    logsFilters.masterSiteId,
    logsFilters.siteId,
    logsFilters.workId,
    logsFilters.technicianId,
    logsFilters.from,
    logsFilters.to,
    logsFilters.limit,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (loadingMeta) {
    return (
      <div className="tech-page">
        <p>Loading maintenance metadata…</p>
      </div>
    );
  }

  // ===== helpers
  const setFormField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const getCurrentLocation = () =>
    new Promise((resolve) => {
      if (window.isSecureContext === false) {
        return resolve({
          error:
            "Location needs a secure (https) connection. This page was opened over plain http, so the phone's browser blocks location access no matter what permission is granted.",
        });
      }
      if (!navigator.geolocation) {
        return resolve({ error: "This browser does not support location access." });
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => {
          const reasons = {
            1: "Location permission was denied for this site in the browser. Check the site settings (not just the phone's app permissions) and allow Location.",
            2: "Location is currently unavailable on this device (GPS/network location off?).",
            3: "Getting the location timed out. Try again with a clearer view of the sky or better signal.",
          };
          resolve({ error: reasons[err.code] || err.message || "Could not get location." });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const handleSitePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setPhotoUploading(true);
      const [res, loc] = await Promise.all([
        axios.post("/api/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }),
        getCurrentLocation(),
      ]);
      const data = res.data || {};
      const url = data.url || data.path || data.location || data.fileUrl || "";
      setFormField("sitePhoto", {
        url,
        originalName: file.name,
        mimeType: file.type || "",
        capturedAt: new Date().toISOString(),
        lat: loc?.lat ?? null,
        lng: loc?.lng ?? null,
      });
      if (loc?.error) {
        alert(`Photo uploaded, but location could not be captured.\n\n${loc.error}`);
      }
    } catch (err) {
      console.error("Photo upload failed", err);
      alert("Failed to upload photo: " + (err?.response?.data?.message || err.message));
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const setItemField = (idx, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === "operation" && value === "replace") items[idx].unitPriceINR = "";
      return { ...prev, items };
    });
  };

  const addItemRow = () =>
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { partId: "", operation: "replace", qty: 1, unitPriceINR: "" }],
    }));

  const removeItemRow = (idx) =>
    setForm((prev) => {
      const items = prev.items.slice();
      items.splice(idx, 1);
      if (items.length === 0) items.push({ partId: "", operation: "replace", qty: 1, unitPriceINR: "" });
      return { ...prev, items };
    });

  const resetCreateForm = () => {
    setTargetType("site");
    setForm({
      masterSiteId: "",
      siteId: "",
      workId: "",
      initialStatus: "",
      action: "",
      items: [{ partId: "", operation: "replace", qty: 1, unitPriceINR: "" }],
      notes: "",
      closeNow: false,
      finalStatus: "",
      sitePhoto: null,

      rawWaterTDS: "",
      finalWaterTDS: "",
      HPPressure: "",
      GoodWaterFlow: "",
      RejectedWaterFlow: "",
      rawWaterTDSNotMeasured: false,
      finalWaterTDSNotMeasured: false,
      HPPressureNotMeasured: false,
      GoodWaterFlowNotMeasured: false,
      RejectedWaterFlowNotMeasured: false,
      SMSsending: false,
      backwashPump: false,
    });
    setSiteSearch("");
    setWorkSearch("");
    setSubmitMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMsg("");

    if (targetType === "site" && !form.siteId) return alert("Select a site");
    if (targetType === "work" && !form.workId) return alert("Select a work");
    if (!form.initialStatus) return alert("Select the initial status");
    if (!form.action) return alert("Select the action");
    if (!form.closeNow) return alert("Select 'Close now' to set final status");
    if (!form.finalStatus) return alert("Select the final status");
    if (!form.sitePhoto?.url) return alert("Take a site photo before saving");

    try {
      setSubmitting(true);

      const base = {
        initialStatus: form.initialStatus,
        action: form.action || "",
        notes: form.notes,
        totalPartsINR: totalPartsINR,

        // ✅ send toggles always as boolean
        SMSsending: !!form.SMSsending,
        backwashPump: !!form.backwashPump,
        ...(form.sitePhoto?.url ? { sitePhoto: form.sitePhoto } : {}),

        items: (form.items || [])
          .filter((i) => i.partId)
          .map((i) => ({
            part: i.partId,
            operation: i.operation,
            qty: Math.max(1, Number(i.qty || 1)),
            ...(i.operation === "repair" ? { unitPriceINR: Number(i.unitPriceINR || 0) } : {}),
          })),

        ...(form.closeNow && form.finalStatus ? { finalStatus: form.finalStatus } : {}),
      };

      // include telemetry only if measured and value present
      const addIfMeasured = (flagKey, valueKey) => {
        const notMeasured = !!form[flagKey];
        const value = form[valueKey];
        if (!notMeasured && value !== "" && value !== undefined && value !== null) {
          base[valueKey] = Number(value);
        }
      };
      addIfMeasured("rawWaterTDSNotMeasured", "rawWaterTDS");
      addIfMeasured("finalWaterTDSNotMeasured", "finalWaterTDS");
      addIfMeasured("HPPressureNotMeasured", "HPPressure");
      addIfMeasured("GoodWaterFlowNotMeasured", "GoodWaterFlow");
      addIfMeasured("RejectedWaterFlowNotMeasured", "RejectedWaterFlow");

      const payload = targetType === "site" ? { ...base, siteId: form.siteId } : { ...base, workId: form.workId };

      await axios.post(`/api/maintenance/logs`, payload, authHeader);

      setSubmitMsg("Saved ✔");
      resetCreateForm();
      await fetchLogs();

      // ✅ close after save
      setShowCreate(false);
    } catch (err) {
      console.error(err);
      alert("Failed to save: " + (err?.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const finalizeLog = async (logId) => {
    const fs = finalStatusDraft[logId];
    if (!fs) return alert("Select a final status first");
    try {
      setFinalizingId(logId);
      await axios.patch(`/api/maintenance/logs/${logId}/final-status`, { finalStatus: fs }, authHeader);
      setFinalStatusDraft((prev) => {
        const cp = { ...prev };
        delete cp[logId];
        return cp;
      });
      fetchLogs();
    } catch (err) {
      console.error("Finalize error", err);
      alert("Failed to set final status: " + (err?.response?.data?.message || err.message));
    } finally {
      setFinalizingId(null);
    }
  };

  const exportCSV = () => {
    if (!displayedLogs || displayedLogs.length === 0) {
      alert("No rows to export");
      return;
    }
    const rows = [];
    if (logsTargetType === "site") {
      rows.push([
        "Recorded At",
        "Master Site",
        "Site",
        "Initial Status",
        "Final Status",
        "Action",
        "Items",
        "Raw TDS (ppm)",
        "Final TDS (ppm)",
        "HP Pressure (bar)",
        "Good Flow (LPM)",
        "Reject Flow (LPM)",
        "SMS",
        "Backwash Completed",
        "Total Parts (INR)",
        "Technician",
        "Notes",
      ]);
    } else {
      rows.push([
        "Recorded At",
        "Work",
        "Location",
        "Initial Status",
        "Final Status",
        "Action",
        "Items",
        "Raw TDS (ppm)",
        "Final TDS (ppm)",
        "HP Pressure (bar)",
        "Good Flow (LPM)",
        "Reject Flow (LPM)",
        "SMS",
        "Backwash Completed",
        "Total Parts (INR)",
        "Technician",
        "Notes",
      ]);
    }

    displayedLogs.forEach((row) => {
      const siteObj = typeof row.siteId === "object" ? row.siteId : siteMap.get(String(row.siteId));
      const siteName = siteObj?.name || "-";
      const msName = logsTargetType === "site" ? masterMap.get(String(siteObj?.masterSiteId))?.name || "-" : "-";

      const workObj = typeof row.workId === "object" ? row.workId : workMap.get(String(row.workId));
      const workName = workObj?.name || "-";
      const workLocation = workObj?.location || "-";

      const itemsStr =
        Array.isArray(row.items) && row.items.length
          ? row.items.map((it) => renderItemLabel(it, meta.parts)).join(" | ")
          : "";

      const common = [
        (row.initialStatus || "").replace(/_/g, " "),
        (row.finalStatus || "").replace(/_/g, " "),
        (row.action || "").replace(/_/g, " "),
        itemsStr,
        showMetric(row.rawWaterTDS, "ppm"),
        showMetric(row.finalWaterTDS, "ppm"),
        showMetric(row.HPPressure, "bar"),
        showMetric(row.GoodWaterFlow, "LPM"),
        showMetric(row.RejectedWaterFlow, "LPM"),
        showBool(row.SMSsending),
        showBool(row.backwashPump),
        Number(row.totalPartsINR || 0).toFixed(2),
        row.technician?.username || "-",
        (row.notes || "").replace(/\r?\n/g, " "),
      ];

      if (logsTargetType === "site") {
        rows.push([formatDateTime(row.recordedAt || row.createdAt), msName, siteName, ...common]);
      } else {
        rows.push([formatDateTime(row.recordedAt || row.createdAt), workName, workLocation, ...common]);
      }
    });

    rows.push([]);
    rows.push([
      "",
      "",
      "",
      "",
      "",
      "",
      "TOTAL ROWS",
      displayedLogs.length,
      "",
      "",
      "",
      "",
      "",
      Number(logsTotals.totalPartsINR).toFixed(2),
      "",
      "",
    ]);

    const csv = rows
      .map((r) =>
        r
          .map((cell) => {
            const str = String(cell ?? "");
            if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
            return str;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.download = `maintenance-logs-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const canCreate = isAdmin || isTechnician || isSuperAdmin;

  return (
    <div className="tech-page">
      {/* ✅ Header + Add button */}
      <div className="tm-header">
        <h2 className="page-title tm-title">Technician Maintenance</h2>

        {canCreate && (
          <button
            className="btn primary"
            onClick={() => {
              resetCreateForm();
              setShowCreate(true);
            }}
          >
            + Add Maintenance
          </button>
        )}
      </div>

      {/* ✅ Create modal (only when needed) */}
      {showCreate && (
        <div
          className="tm-modal-overlay"
          onMouseDown={(e) => {
            if (e.target.classList.contains("tm-modal-overlay")) setShowCreate(false);
          }}
        >
          <section className="card tm-modal">
            <div className="tm-modal-header">
              <div className="card-title" style={{ margin: 0 }}>
                Create Maintenance Update
              </div>
              <button className="tm-modal-close" onClick={() => setShowCreate(false)} aria-label="Close">
                ✕
              </button>
            </div>

            {!canCreate ? (
              <div className="muted">You have read-only access. Contact admin to grant technician role.</div>
            ) : (
              <form onSubmit={handleSubmit} className="form tm-form-compact">
                {/* Target toggle */}
                <div className="form-group inline">
                  <label>Maintenance Target</label>
                  <div className="row gap">
                    <label className="checkbox label-inline">
                      <input
                        type="radio"
                        name="targetType"
                        checked={targetType === "site"}
                        onChange={() => {
                          setTargetType("site");
                          setForm((f) => ({ ...f, workId: "" }));
                        }}
                      />{" "}
                      Site
                    </label>
                    <label className="checkbox label-inline">
                      <input
                        type="radio"
                        name="targetType"
                        checked={targetType === "work"}
                        onChange={() => {
                          setTargetType("work");
                          setForm((f) => ({ ...f, siteId: "", masterSiteId: "" }));
                        }}
                      />{" "}
                      Work
                    </label>
                  </div>
                </div>

                {/* Site / Work */}
                {targetType === "site" && (
                  <>
                    <div className="form-group inline">
                      <label>Master Site</label>
                      <select
                        value={form.masterSiteId}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, masterSiteId: e.target.value, siteId: "" }))
                        }
                      >
                        <option value="">— Any Master Site —</option>
                        {masterSites.map((ms) => (
                          <option key={ms._id} value={ms._id}>
                            {ms.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group inline">
                      <label>Site</label>
                      <input
                        type="text"
                        placeholder="Search site…"
                        value={siteSearch}
                        onChange={(e) => setSiteSearch(e.target.value)}
                        className="tm-mini-input"
                      />
                      <select value={form.siteId} onChange={(e) => setFormField("siteId", e.target.value)}>
                        <option value="">— Select Site —</option>
                        {filteredSitesForForm.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {targetType === "work" && (
                  <div className="form-group inline">
                    <label>Work</label>
                    <input
                      type="text"
                      placeholder="Search work…"
                      value={workSearch}
                      onChange={(e) => setWorkSearch(e.target.value)}
                      className="tm-mini-input"
                    />
                    <select value={form.workId} onChange={(e) => setFormField("workId", e.target.value)}>
                      <option value="">— Select Work —</option>
                      {filteredWorksForForm.map((w) => (
                        <option key={w._id} value={w._id}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Initial Status + Action */}
                <div className="row-2">
                  <div className="form-group">
                    <label>Status Before</label>
                    <select value={form.initialStatus} onChange={(e) => setFormField("initialStatus", e.target.value)}>
                      <option value="">— Select —</option>
                      {meta.statuses.map((s) => (
                        <option key={s} value={s}>
                          {capitalizeWords(s.replace(/_/g, " "))}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Action</label>
                    <select value={form.action} onChange={(e) => setFormField("action", e.target.value)}>
                      <option value="">— Select —</option>
                      {meta.actions.map((a) => (
                        <option key={a} value={a}>
                          {capitalizeWords(a.replace(/_/g, " "))}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Parts table */}
                <div className="form-group">
                  <div className="row-between">
                    <label>Parts / Work Items</label>
                    <button type="button" className="btn" onClick={addItemRow}>
                      + Add Row
                    </button>
                  </div>

                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Part</th>
                          <th>Operation</th>
                          <th>Qty</th>
                          <th>Unit (₹)</th>
                          <th>Subtotal (₹)</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li, idx) => {
                          const isRepair = form.items[idx].operation === "repair";
                          return (
                            <tr key={idx}>
                              <td style={{ minWidth: "220px" }}>
                                <Select
                                  options={partOptions}
                                  placeholder="Search part…"
                                  value={partOptions.find((o) => o.value === form.items[idx].partId) || null}
                                  onChange={(selected) => setItemField(idx, "partId", selected ? selected.value : "")}
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                              </td>
                              <td>
                                <select
                                  value={form.items[idx].operation}
                                  onChange={(e) => setItemField(idx, "operation", e.target.value)}
                                >
                                  <option value="replace">Replace</option>
                                  <option value="repair">Repair</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min={1}
                                  value={form.items[idx].qty}
                                  onChange={(e) => setItemField(idx, "qty", e.target.value)}
                                />
                              </td>
                              <td>
                                {isRepair ? (
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    placeholder="Repair cost"
                                    value={form.items[idx].unitPriceINR}
                                    onChange={(e) => setItemField(idx, "unitPriceINR", e.target.value)}
                                  />
                                ) : (
                                  <span title="Auto from part price">{Number(li.previewUnit || 0).toFixed(2)}</span>
                                )}
                              </td>
                              <td className="bold">{Number(li.subtotalINR || 0).toFixed(2)}</td>
                              <td>
                                <button type="button" className="link danger" onClick={() => removeItemRow(idx)}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="total-row">
                    <div>Items: {lineItems.length}</div>
                    <div className="total">Total Parts: ₹ {Number(totalPartsINR).toFixed(2)}</div>
                  </div>
                </div>

                {/* ✅ COMPACT: Water Quality & Flow (same data, less space) */}
                <div className="telemetry-section compact">
                  <div className="tm-sec-head">
                    <h4>Water Quality & Flow</h4>
                    <div className="tm-mini-toggles">
                      <label className="mini-check">
                        <input
                          type="checkbox"
                          checked={form.SMSsending ?? false}
                          onChange={(e) => setFormField("SMSsending", e.target.checked)}
                        />
                        SMS
                      </label>
                      <label className="mini-check">
                        <input
                          type="checkbox"
                          checked={form.backwashPump ?? false}
                          onChange={(e) => setFormField("backwashPump", e.target.checked)}
                        />
                        Backwash
                      </label>
                    </div>
                  </div>

                  <div className="telemetry-grid">
                    <TelemetryField
                      label="Raw TDS"
                      placeholder="ppm"
                      value={form.rawWaterTDS}
                      disabled={form.rawWaterTDSNotMeasured}
                      checked={form.rawWaterTDSNotMeasured}
                      onValue={(v) => setFormField("rawWaterTDS", v)}
                      onCheck={(ck) =>
                        setForm((p) => ({
                          ...p,
                          rawWaterTDSNotMeasured: ck,
                          rawWaterTDS: ck ? "" : p.rawWaterTDS,
                        }))
                      }
                    />
                    <TelemetryField
                      label="Final TDS"
                      placeholder="ppm"
                      value={form.finalWaterTDS}
                      disabled={form.finalWaterTDSNotMeasured}
                      checked={form.finalWaterTDSNotMeasured}
                      onValue={(v) => setFormField("finalWaterTDS", v)}
                      onCheck={(ck) =>
                        setForm((p) => ({
                          ...p,
                          finalWaterTDSNotMeasured: ck,
                          finalWaterTDS: ck ? "" : p.finalWaterTDS,
                        }))
                      }
                    />
                    <TelemetryField
                      label="HP"
                      placeholder="bar"
                      value={form.HPPressure}
                      disabled={form.HPPressureNotMeasured}
                      checked={form.HPPressureNotMeasured}
                      onValue={(v) => setFormField("HPPressure", v)}
                      onCheck={(ck) =>
                        setForm((p) => ({
                          ...p,
                          HPPressureNotMeasured: ck,
                          HPPressure: ck ? "" : p.HPPressure,
                        }))
                      }
                    />
                    <TelemetryField
                      label="Good Flow"
                      placeholder="LPM"
                      value={form.GoodWaterFlow}
                      disabled={form.GoodWaterFlowNotMeasured}
                      checked={form.GoodWaterFlowNotMeasured}
                      onValue={(v) => setFormField("GoodWaterFlow", v)}
                      onCheck={(ck) =>
                        setForm((p) => ({
                          ...p,
                          GoodWaterFlowNotMeasured: ck,
                          GoodWaterFlow: ck ? "" : p.GoodWaterFlow,
                        }))
                      }
                    />
                    <TelemetryField
                      label="Reject Flow"
                      placeholder="LPM"
                      value={form.RejectedWaterFlow}
                      disabled={form.RejectedWaterFlowNotMeasured}
                      checked={form.RejectedWaterFlowNotMeasured}
                      onValue={(v) => setFormField("RejectedWaterFlow", v)}
                      onCheck={(ck) =>
                        setForm((p) => ({
                          ...p,
                          RejectedWaterFlowNotMeasured: ck,
                          RejectedWaterFlow: ck ? "" : p.RejectedWaterFlow,
                        }))
                      }
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    rows={2}
                    placeholder="Present status, observations, etc."
                    value={form.notes}
                    onChange={(e) => setFormField("notes", e.target.value)}
                  />
                </div>

                {/* Finalize on create */}
                <div className="row-2">
                  <div className="form-group">
                    <label className="checkbox label-inline">
                      <input
                        type="checkbox"
                        checked={form.closeNow}
                        onChange={(e) => setFormField("closeNow", e.target.checked)}
                      />
                      Close now (set Status After)
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Status After</label>
                    <select value={form.finalStatus} onChange={(e) => setFormField("finalStatus", e.target.value)}>
                      <option value="">— Select —</option>
                      {meta.statuses.map((s) => (
                        <option key={s} value={s}>
                          {capitalizeWords(s.replace(/_/g, " "))}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Site Photo */}
                <div className="form-group">
                  <label>Site Photo *</label>
                  <div className="row gap" style={{ alignItems: "center" }}>
                    <input
                      ref={sitePhotoInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleSitePhotoUpload}
                      disabled={photoUploading}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      className="btn"
                      onClick={() => sitePhotoInputRef.current?.click()}
                      disabled={photoUploading}
                    >
                      {photoUploading ? "Capturing…" : "Take Photo"}
                    </button>
                    {form.sitePhoto?.url && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <a
                          href={makeFullUrl(form.sitePhoto.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={makeFullUrl(form.sitePhoto.url)}
                            alt="Site"
                            style={{ height: 96, width: 96, objectFit: "cover", borderRadius: 4 }}
                          />
                        </a>
                        <div className="muted" style={{ fontSize: 12 }}>
                          <div>{formatDateTime(form.sitePhoto.capturedAt)}</div>
                          <div>
                            {form.sitePhoto.lat != null && form.sitePhoto.lng != null ? (
                              <a
                                href={`https://www.google.com/maps?q=${form.sitePhoto.lat},${form.sitePhoto.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {`${form.sitePhoto.lat.toFixed(5)}, ${form.sitePhoto.lng.toFixed(5)}`}
                              </a>
                            ) : (
                              "Location unavailable"
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="link danger"
                          onClick={() => setFormField("sitePhoto", null)}
                          style={{ alignSelf: "flex-start" }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="row gap">
                  <button type="submit" className="btn primary" disabled={submitting}>
                    {submitting ? "Saving…" : "Save Update"}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      resetCreateForm();
                      setShowCreate(false);
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  {submitMsg && <span className="success">{submitMsg}</span>}
                </div>
              </form>
            )}
          </section>
        </div>
      )}

      {/* ✅ Logs always visible */}
      <section className="card" style={{ marginTop: 14 }}>
        <div className="card-title">Recent Maintenance Logs</div>

        {/* Logs target toggle */}
        <div className="form-group inline">
          <label>Filter Target</label>
          <div className="row gap">
            <label className="checkbox label-inline">
              <input
                type="radio"
                name="logsTargetType"
                checked={logsTargetType === "site"}
                onChange={() => {
                  setLogsTargetType("site");
                  setLogsFilters((p) => ({ ...p, workId: "" }));
                }}
              />{" "}
              Site
            </label>
            <label className="checkbox label-inline">
              <input
                type="radio"
                name="logsTargetType"
                checked={logsTargetType === "work"}
                onChange={() => {
                  setLogsTargetType("work");
                  setLogsFilters((p) => ({ ...p, masterSiteId: "", siteId: "" }));
                }}
              />{" "}
              Work
            </label>
          </div>
        </div>

        {/* Filters */}
        <div className="filters">
          <div className="row-2">
            <div className="form-group">
              <label>Master Site</label>
              <select
                value={logsFilters.masterSiteId}
                disabled={logsTargetType === "work"}
                onChange={(e) => {
                  const msId = e.target.value;
                  setLogsFilters((p) => ({ ...p, masterSiteId: msId, siteId: "" }));
                }}
              >
                <option value="">— Any —</option>
                {masterSites.map((ms) => (
                  <option key={ms._id} value={ms._id}>
                    {ms.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Site</label>
              <select
                value={logsFilters.siteId}
                disabled={logsTargetType === "work"}
                onChange={(e) => setLogsFilters((p) => ({ ...p, siteId: e.target.value }))}
              >
                <option value="">— Any —</option>
                {(logsFilters.masterSiteId ? sitesForSelectedMaster : sites).map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Work</label>
              <select
                value={logsFilters.workId}
                disabled={logsTargetType === "site"}
                onChange={(e) => setLogsFilters((prev) => ({ ...prev, workId: e.target.value }))}
              >
                <option value="">— Any —</option>
                {works.map((w) => (
                  <option key={w._id} value={w._id}>
                    {w.name} {w.location ? `— ${w.location}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isAdmin && (
            <div className="form-group">
              <label>Technician</label>
              <select
                value={logsFilters.technicianId}
                onChange={(e) => setLogsFilters((p) => ({ ...p, technicianId: e.target.value }))}
              >
                <option value="">— Any —</option>
                {users.map((u) => (
                  <option key={u._id || u.id} value={u._id || u.id}>
                    {u.username} ({u.role})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="row-2">
            <div className="form-group">
              <label>From</label>
              <input
                type="date"
                value={logsFilters.from}
                onChange={(e) => setLogsFilters((p) => ({ ...p, from: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>To</label>
              <input
                type="date"
                value={logsFilters.to}
                onChange={(e) => setLogsFilters((p) => ({ ...p, to: e.target.value }))}
              />
            </div>
          </div>

          <div className="row gap">
            <select
              value={logsFilters.limit}
              onChange={(e) => setLogsFilters((p) => ({ ...p, limit: Number(e.target.value) }))}
            >
              {[10, 25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} rows
                </option>
              ))}
            </select>
            <button className="btn" onClick={fetchLogs}>
              {logsLoading ? "Loading…" : "Refresh"}
            </button>
            <button className="btn" onClick={exportCSV}>
              Export CSV
            </button>
          </div>
        </div>

        <br />

        {/* Table */}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                {logsTargetType === "site" ? (
                  <>
                    <th>Master Site</th>
                    <th>Site</th>
                  </>
                ) : (
                  <>
                    <th>Work</th>
                    <th>Location</th>
                  </>
                )}
                <th>Photo</th>
                <th>Initial</th>
                <th>Final</th>
                <th>Action</th>
                <th>Items</th>
                <th>Total (₹)</th>
                <th>Technician</th>
                <th>Finalize</th>
                <th>Raw TDS (ppm)</th>
                <th>Final TDS (ppm)</th>
                <th>HP Pressure (bar)</th>
                <th>Good Flow (LPM)</th>
                <th>Reject Flow (LPM)</th>
                <th>SMS</th>
                <th>Backwash Completed</th>
              </tr>
            </thead>
            <tbody>
              {displayedLogs.map((row) => {
                const siteObj = typeof row.siteId === "object" ? row.siteId : siteMap.get(String(row.siteId));
                const siteName = siteObj?.name || "—";
                const msName =
                  logsTargetType === "site"
                    ? masterMap.get(String(siteObj?.masterSiteId))?.name || "—"
                    : "—";

                const workObj = typeof row.workId === "object" ? row.workId : workMap.get(String(row.workId));
                const workName = workObj?.name || "—";
                const workLocation = workObj?.location || "—";

                const isClosed = !!row.finalStatus || row.isClosed;
                const draft = finalStatusDraft[row._id] || "";

                return (
                  <tr key={row._id}>
                    <td className="nowrap">{formatDateTime(row.recordedAt || row.createdAt)}</td>

                    {logsTargetType === "site" ? (
                      <>
                        <td>{msName}</td>
                        <td>{siteName}</td>
                      </>
                    ) : (
                      <>
                        <td>{workName}</td>
                        <td>{workLocation}</td>
                      </>
                    )}

                    <td>
                      {row.sitePhoto?.url ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <a
                            href={makeFullUrl(row.sitePhoto.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={makeFullUrl(row.sitePhoto.url)}
                              alt="Site"
                              style={{ height: 36, width: 36, objectFit: "cover", borderRadius: 4 }}
                            />
                          </a>
                          <div className="muted" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                            <div>{formatDateTime(row.sitePhoto.capturedAt)}</div>
                            <div>
                              {row.sitePhoto.lat != null && row.sitePhoto.lng != null ? (
                                <a
                                  href={`https://www.google.com/maps?q=${row.sitePhoto.lat},${row.sitePhoto.lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {`${row.sitePhoto.lat.toFixed(5)}, ${row.sitePhoto.lng.toFixed(5)}`}
                                </a>
                              ) : (
                                "Location unavailable"
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td>{capitalizeWords(String(row.initialStatus || "").replace(/_/g, " "))}</td>
                    <td>{row.finalStatus ? capitalizeWords(String(row.finalStatus).replace(/_/g, " ")) : "—"}</td>
                    <td>{capitalizeWords(String(row.action || "").replace(/_/g, " "))}</td>

                    <td>
                      {Array.isArray(row.items) && row.items.length > 0 ? (
                        <ul className="list">
                          {row.items.map((it, idx) => (
                            <li key={idx}>{renderItemLabel(it, meta.parts)}</li>
                          ))}
                        </ul>
                      ) : (
                        "—"
                      )}
                      {row.notes ? <div className="notes">{row.notes}</div> : null}
                    </td>

                    <td className="bold">₹ {Number(row.totalPartsINR || 0).toFixed(2)}</td>
                    <td>{row.technician?.username || "—"}</td>

                    <td>
                      {isClosed ? (
                        <span className="muted">Closed</span>
                      ) : (
                        <div className="row gap">
                          <select
                            value={draft}
                            onChange={(e) =>
                              setFinalStatusDraft((prev) => ({ ...prev, [row._id]: e.target.value }))
                            }
                          >
                            <option value="">— Final Status —</option>
                            {meta.statuses.map((s) => (
                              <option key={s} value={s}>
                                {capitalizeWords(s.replace(/_/g, " "))}
                              </option>
                            ))}
                          </select>
                          <button
                            className="btn small"
                            disabled={finalizingId === row._id}
                            onClick={() => finalizeLog(row._id)}
                          >
                            {finalizingId === row._id ? "Saving…" : "Set"}
                          </button>
                        </div>
                      )}
                    </td>

                    <td>{showMetric(row.rawWaterTDS, "ppm")}</td>
                    <td>{showMetric(row.finalWaterTDS, "ppm")}</td>
                    <td>{showMetric(row.HPPressure, "bar")}</td>
                    <td>{showMetric(row.GoodWaterFlow, "LPM")}</td>
                    <td>{showMetric(row.RejectedWaterFlow, "LPM")}</td>
                    <td>{showBool(row.SMSsending)}</td>
                    <td>{showBool(row.backwashPump)}</td>
                  </tr>
                );
              })}

              {displayedLogs.length === 0 && (
                <tr>
                  <td colSpan={17} className="muted center">
                    No logs
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="row-between" style={{ marginTop: "8px" }}>
          <div className="muted">
            Showing <b>{logsTotals.count}</b> log{logsTotals.count === 1 ? "" : "s"}
          </div>
          <div className="bold">
            Total Cost (₹): <span>₹ {logsTotals.totalPartsINR.toFixed(2)}</span>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Small helper component for compact telemetry ---------- */
function TelemetryField({ label, placeholder, value, disabled, checked, onValue, onCheck }) {
  return (
    <div className="telemetry-item">
      <div className="telemetry-label">{label}</div>
      <input
        className="telemetry-input"
        type="number"
        disabled={disabled}
        value={value || ""}
        onChange={(e) => onValue(e.target.value || "")}
        placeholder={placeholder}
      />
      <label className="mini-check" title="Not measured">
        <input type="checkbox" checked={!!checked} onChange={(e) => onCheck(e.target.checked)} />
        NM
      </label>
    </div>
  );
}

// ===== util helpers =====
function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function renderItemLabel(item, parts) {
  const partId = typeof item.part === "object" ? item.part?._id : item.part || item.partId;
  const p = (parts || []).find((x) => String(x._id) === String(partId));
  const label = item.part?.label || p?.label || "Part";
  const qty = item.qty ?? 1;
  const unit = item.unitPriceINR ?? 0;
  const sub = item.subtotalINR ?? qty * unit;
  const op = item.operation === "repair" ? "Repair" : "Replace";
  return `${label} (${op}) × ${qty} @ ₹${Number(unit).toFixed(2)} = ₹${Number(sub).toFixed(2)}`;
}

function capitalizeWords(str) {
  return String(str || "")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function showMetric(v, unit = "") {
  if (v === undefined || v === null || v === "") return "Not measured";
  const num = Number(v);
  return Number.isNaN(num) ? "Not measured" : `${num}${unit ? ` ${unit}` : ""}`;
}

function showBool(v) {
  if (v === undefined || v === null) return "—";
  return v ? "Yes" : "No";
}